import {
  ArrowLeftRight,
  ArrowRightLeft,
  Beer,
  Bell,
  BookOpen,
  BookOpenCheck,
  BadgeDollarSign,
  CalendarRange,
  ShoppingCart,
  Sparkles,
  Bot,
  Building2,
  MessageCircle,
  CreditCard,
  HandCoins,
  History,
  Inbox,
  Landmark,
  Library,
  LifeBuoy,
  Link2,
  ListChecks,
  PackageSearch,
  Percent,
  Printer,
  ReceiptText,
  Scale,
  Store,
  Table2,
  Ticket,
  Truck,
  UserRound,
  Users,
} from "lucide-react";
import type { FeaturePage } from "./types";
import { lazyPage } from "../errors";

/**
 * Feature pages. The navigation needs only the metadata below; each page's
 * code (and its CSS) loads the first time it is opened, so the initial
 * bundle does not grow with the number of features.
 */
/** Hidden from people whose "see costs" switch an owner turned off (the server refuses them anyway). */
const seesCosts = (s: Parameters<NonNullable<FeaturePage["visible"]>>[0]) => s.role === "owner" || (s.permissions as any)?.seeCosts !== false;
const seesReports = (s: Parameters<NonNullable<FeaturePage["visible"]>>[0]) => s.role === "owner" || (s.permissions as any)?.viewReports !== false;

export const featurePages: FeaturePage[] = [
  {
    // The inventory ↔ recipe ↔ POS map: completeness, checks with fixes, review queue, size/modifier rules (docs/features/mapping.md).
    id: "mapping",
    label: "Drink mapping",
    keywords: ["pos mapping", "map", "modifiers", "sizes", "double", "unmapped", "integrity", "pos items"],
    icon: Link2,
    section: "workspace",
    group: "menu",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./mapping/MappingPage").then((m) => ({ default: m.MappingPage })),
    ),
  },
  {
    // Sam, the in-app assistant: questions, diagnostics and confirmed fixes (docs/features/concierge-ai.md).
    // The menu shows the company's name for it (Sam unless an owner renamed it).
    id: "assistant",
    label: "Sam",
    labelFor: (s) => s.assistantName || "Sam",
    keywords: ["assistant", "help", "ask", "ai", "chat", "questions"],
    icon: MessageCircle,
    section: "workspace",
    group: "assistant",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./assistant/AssistantPage").then((m) => ({ default: m.AssistantPage })),
    ),
  },
  {
    // Only when the company has more than one bar (single bars keep the simple product).
    id: "company",
    label: "Company overview",
    icon: Building2,
    section: "workspace",
    group: "company",
    roles: ["owner", "manager"],
    visible: (s) => (s.venues?.length ?? 0) > 1 && s.venueScope === "all",
    component: lazyPage(() =>
      import("./Company").then((m) => ({ default: m.CompanyOverview })),
    ),
  },
  {
    // The alerts inbox and each person's alert choices (docs/features/notifications.md).
    id: "alerts",
    label: "Alerts",
    icon: Bell,
    section: "workspace",
    group: "company",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./notifications/AlertsPage").then((m) => ({
        default: m.AlertsPage,
      })),
    ),
  },
  {
    id: "invoices",
    label: "Invoices",
    icon: ReceiptText,
    section: "workspace",
    group: "buying",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./Invoices").then((m) => ({ default: m.page.component })),
    ),
  },
  {
    id: "suppliers",
    label: "Suppliers & ordering",
    icon: Truck,
    section: "workspace",
    group: "buying",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./Suppliers").then((m) => ({ default: m.page.component })),
    ),
  },
  {
    // Distributor terms, bills due, payments (features/accounting).
    id: "bills",
    label: "Bills & terms",
    icon: HandCoins,
    section: "workspace",
    group: "buying",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./accounting/BillsPage").then((m) => ({ default: m.BillsPage })),
    ),
  },
  {
    id: "par-review",
    label: "Stockouts & pars",
    icon: PackageSearch,
    section: "workspace",
    group: "count",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./counting/ParReview").then((m) => ({
        default: m.page.component,
      })),
    ),
  },
  {
    // Moving stock between a company's bars; single bars have nothing to transfer.
    id: "transfers",
    label: "Transfers between bars",
    icon: ArrowLeftRight,
    section: "workspace",
    group: "buying",
    roles: ["owner", "manager", "auditor"],
    visible: (s) => (s.venues?.length ?? 0) > 1,
    component: lazyPage(() =>
      import("./transfers/TransfersPage").then((m) => ({
        default: m.TransfersPage,
      })),
    ),
  },
  {
    id: "costing",
    label: "Menu & margins",
    icon: Percent,
    section: "workspace",
    group: "menu",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./Costing").then((m) => ({ default: m.page.component })),
    ),
  },
  {
    id: "deposits",
    label: "Kegs & deposits",
    icon: Beer,
    section: "workspace",
    group: "inventory",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./Deposits").then((m) => ({ default: m.page.component })),
    ),
  },
  {
    // The shared NC ABC reference catalog: photos, add from catalog, quarterly price changes.
    id: "nc-catalog",
    label: "NC ABC catalog",
    icon: Library,
    section: "workspace",
    group: "inventory",
    roles: ["owner", "manager", "auditor", "counter"],
    component: lazyPage(() =>
      import("./catalog/CatalogPage").then((m) => ({ default: m.CatalogPage })),
    ),
  },
  {
    // Scales: connect, weekly check-weight, supported models (docs/features/scale.md).
    id: "scales",
    label: "Scales",
    icon: Scale,
    section: "workspace",
    group: "count",
    roles: ["owner", "manager", "auditor", "counter"],
    component: lazyPage(() =>
      import("../scale/ScalesPage").then((m) => ({ default: m.ScalesPage })),
    ),
  },
  {
    // Stock on any date, estimate today, variance by area, audit log.
    id: "history",
    label: "History",
    icon: History,
    section: "workspace",
    group: "reports",
    roles: ["owner", "manager", "auditor", "counter"],
    component: lazyPage(() =>
      import("./history/HistoryPage").then((m) => ({ default: m.HistoryPage })),
    ),
  },
  {
    // Spec cards (no costs) and QR shelf labels for DYMO/Brother/sheets (docs/features/operator-ux.md).
    id: "print",
    label: "Spec cards & labels",
    icon: Printer,
    section: "workspace",
    group: "count",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./staff/PrintPage").then((m) => ({
        default: m.printPage.component,
      })),
    ),
  },
  {
    // Every list as a spreadsheet: saved views, filters, groups, xlsx/CSV (docs/features/grids.md).
    id: "sheets",
    label: "Spreadsheets",
    keywords: ["grid", "views", "saved views", "export", "excel", "xlsx", "csv", "report center", "sales lines", "movements"],
    icon: Table2,
    section: "workspace",
    group: "reports",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("../grid/SheetsPage").then((m) => ({ default: m.SheetsPage })),
    ),
  },
  {
    id: "accounting",
    label: "Accounting export",
    icon: BookOpenCheck,
    section: "business",
    group: "reports",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./history/AccountingPage").then((m) => ({
        default: m.AccountingPage,
      })),
    ),
  },
  {
    // QuickBooks/Xero sync and report emails (features/accounting).
    id: "books",
    label: "Books & report emails",
    icon: Landmark,
    section: "business",
    group: "reports",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./accounting/BooksPage").then((m) => ({ default: m.BooksPage })),
    ),
  },
  {
    id: "billing",
    label: "Plan & billing",
    icon: CreditCard,
    section: "business",
    group: "settings",
    roles: ["owner"],
    component: lazyPage(() =>
      import("./Billing").then((m) => ({ default: m.page.component })),
    ),
  },
  {
    id: "venues",
    label: "Venues & company",
    icon: Store,
    section: "business",
    group: "company",
    roles: ["owner", "manager"],
    visible: (s) => s.role === "owner" || s.venueScope === "all",
    component: lazyPage(() =>
      import("./Company").then((m) => ({ default: m.VenuesPage })),
    ),
  },
  {
    id: "admin-companies",
    label: "Companies (staff)",
    icon: LifeBuoy,
    section: "business",
    group: "admin",
    roles: ["owner", "manager", "counter", "auditor"],
    visible: (s) => !!s.platformAdmin,
    component: lazyPage(() =>
      import("./Company").then((m) => ({ default: m.AdminCompanies })),
    ),
  },
  {
    id: "invites",
    label: "Invites",
    icon: Ticket,
    section: "business",
    group: "admin",
    roles: ["owner", "manager", "counter", "auditor"],
    visible: (s) => !!s.platformAdmin,
    component: lazyPage(() =>
      import("./Invites").then((m) => ({ default: m.page.component })),
    ),
  },
  {
    id: "concierge-inbox",
    label: "Sales & support",
    icon: Inbox,
    section: "business",
    roles: ["owner", "manager", "counter", "auditor"],
    visible: (s) => !!s.platformAdmin,
    component: lazyPage(() =>
      import("./concierge/Inbox").then((m) => ({ default: m.ConciergeInbox })),
    ),
  },
  {
    // Move products, pars, suppliers and draft recipes over from WISK, BevSpot, Partender or Backbar.
    id: "switching",
    label: "Switch from another app",
    icon: ArrowRightLeft,
    section: "business",
    group: "company",
    roles: ["owner", "manager"],
    visible: (s) => s.role === "owner" || s.venueScope === "all",
    component: lazyPage(() =>
      import("./importers/SwitchingPage").then((m) => ({
        default: m.switchingPage.component,
      })),
    ),
  },
  {
    // Read-only questions from Claude or ChatGPT through owner-issued tokens.
    id: "ai-access",
    label: "AI assistant access",
    icon: Bot,
    section: "business",
    group: "settings",
    roles: ["owner"],
    component: lazyPage(() =>
      import("./account/AiAccess").then((m) => ({
        default: m.aiAccessPage.component,
      })),
    ),
  },
  {
    id: "account",
    label: "Your account",
    icon: UserRound,
    section: "business",
    group: "settings",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./account/AccountPage").then((m) => ({
        default: m.accountPage.component,
      })),
    ),
  },
  {
    // Simple mode per person, who may receive/prep, count auto-lock (docs/features/operator-ux.md).
    id: "staff-mode",
    label: "Staff mode & locks",
    icon: Users,
    section: "business",
    group: "settings",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./staff/StaffSettings").then((m) => ({
        default: m.staffSettingsPage.component,
      })),
    ),
  },
  {
    // Cost changes across the catalog (docs/features/cost-changes.md).
    id: "cost-changes",
    label: "Cost changes",
    keywords: ["price changes", "price increase", "cost per unit", "went up"],
    icon: BadgeDollarSign,
    section: "workspace",
    group: "buying",
    roles: ["owner", "manager", "auditor"],
    visible: seesCosts,
    component: lazyPage(() =>
      import("./costChanges/CostChangesPage").then((m) => ({ default: m.CostChangesPage })),
    ),
  },
  {
    // Purchases for owners: period vs previous, suppliers, categories, weeks (docs/features/purchases.md).
    id: "purchases",
    label: "Purchases",
    keywords: ["spend", "spending", "by supplier", "by distributor", "purchases by week"],
    icon: ShoppingCart,
    section: "workspace",
    group: "buying",
    roles: ["owner", "manager", "auditor"],
    visible: (s) => seesCosts(s) && seesReports(s),
    component: lazyPage(() =>
      import("./purchases/PurchasesPage").then((m) => ({ default: m.PurchasesPage })),
    ),
  },
  {
    // Data-quality filters, completeness meters, Used in (docs/features/cleanup.md).
    id: "cleanup",
    label: "Clean-up",
    keywords: ["duplicates", "missing cost", "barcode", "completeness", "used in", "data quality"],
    icon: Sparkles,
    section: "workspace",
    group: "inventory",
    roles: ["owner", "manager", "auditor"],
    component: lazyPage(() =>
      import("./cleanup/CleanupPage").then((m) => ({ default: m.CleanupPage })),
    ),
  },
  {
    // Sales by business day and week (docs/features/sales-by-day.md).
    id: "sales",
    label: "Sales by day",
    keywords: ["sales", "by week", "daily sales", "unmapped", "last sale imported"],
    icon: CalendarRange,
    section: "workspace",
    group: "reports",
    roles: ["owner", "manager", "auditor"],
    visible: seesReports,
    component: lazyPage(() =>
      import("./salesDays/SalesByDayPage").then((m) => ({ default: m.SalesByDayPage })),
    ),
  },
  {
    id: "cheat-sheet",
    label: "Cheat sheet",
    icon: ListChecks,
    section: "business",
    group: "settings",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./help/CheatSheet").then((m) => ({
        default: m.cheatSheetPage.component,
      })),
    ),
  },
  {
    id: "help",
    label: "Help & glossary",
    icon: BookOpen,
    section: "business",
    group: "settings",
    roles: ["owner", "manager", "counter", "auditor"],
    component: lazyPage(() =>
      import("./onboarding/Help").then((m) => ({
        default: m.helpPage.component,
      })),
    ),
  },
];
export type { FeaturePage };
